import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactStars from 'react-rating-stars-component';
import styles from './index.module.scss';
import { publishReview } from '@/redux/actions';
import { toastNotifyError, toastNotifySuccess } from '../Common/Toast';
import { publishReviewToastNotificationText } from '../../utils/publishReviewToastNotificationText';
import {
  getImageUrlFromCompressedImages,
  imgSizeEnums,
} from '@/utils/compressedImagesHelpers';
import BasicModal from '../BasicModal';
import { getThemeColor } from 'src/utils/getThemeColor';
import { PlusIcon } from 'src/assets/svgExports/PlusIcon';
import { Close } from '@mui/icons-material';
import { REVIEW_MEDIA_TYPE, REVIEW_RATING_STATUS } from '../ReviewsSection/constants';
import CustomizedButton from '../WidgetMaker/WidgetDnD/WIdgetPropertiesBasic/components/Button';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import { getAuthToken } from 'src/utils/getAuthToken';
import { IS_MOBILE } from 'src/utils/checkClientDeviceType';
import classNames from 'classnames';
import { UploadProgress } from './UploadProgress';
import { uploadCustomerMediaToS3 } from '@/utils/uploadCustomerMediaToS3';
import MediaWithRetry from './MediaWithRetry';
import { CHARS_OR_SPACE_REGEX } from 'src/utils/constants';

const MEDIA_LIMIT = 3;

const CustomerReviewModal = ({
  product,
  open,
  onClose,
  loggedInUserReview,
  onSubmit,
  order_id,
  ratingFromProps = null,
}) => {
  const themeColor = getThemeColor() || '#000000';

  const dispatch = useDispatch();
  const { storeInfo, userData, reviewSettings } = useSelector((state) => ({
    storeInfo: state.storeReducer.store,
    userData: state.userReducer.data,
    reviewSettings: state.catalogReducer.reviewSettings,
  }));

  const [reviewState, setReviewState] = useState({
    full_name: loggedInUserReview?.full_name || '',
    review: loggedInUserReview?.review || '',
    rating: ratingFromProps || loggedInUserReview?.rating || 0,
    mediaList: loggedInUserReview?.store_item_review_media || [],
  });

  const [isUploading, setIsUplading] = useState(false);

  const mediaInputRef = useRef(null);

  const imageUrl = product?.thumbnail_url || product?.image_url;
  const compressedImages = product?.images?.[0]?.compressed_images;

  const mediaLimitReached = reviewState?.mediaList?.length === MEDIA_LIMIT;
  const isLastMediaUpoading =
    reviewState?.mediaList?.length === MEDIA_LIMIT - 1 && isUploading;

  const disableSubmitBtn = useMemo(
    () => !reviewState.review || !reviewState.full_name || !reviewState.rating,
    [reviewState]
  );

  //   useEffect(() => {
  //   if (loggedInUserReview && !orderStatusPage) {
  //     setReviewState({
  //       full_name: loggedInUserReview?.full_name,
  //       review: loggedInUserReview?.review,
  //       rating: loggedInUserReview?.rating,
  //       mediaList: loggedInUserReview?.store_item_review_media,
  //     });
  //   }
  // }, [loggedInUserReview, orderStatusPage]);

  useEffect(() => {
    if (open) {
      setReviewState({
        full_name: loggedInUserReview?.full_name || '',
        review: loggedInUserReview?.review || '',
        rating: ratingFromProps || loggedInUserReview?.rating || 0,
        mediaList: loggedInUserReview?.store_item_review_media || [],
      });
    }
  }, [open, ratingFromProps]);

  function handleRatingChange(rating) {
    setReviewState((prevState) => ({
      ...prevState,
      rating,
    }));
  }

  const handleChange = (e) => {
    const inputValue = e.target.value;
    const inputFieldName = e.target.name;

    if (inputFieldName === 'full_name' && !CHARS_OR_SPACE_REGEX.test(inputValue)) {
      return;
    }

    if ((inputValue && inputValue.trim()) || inputValue.length === 0) {
      const maxLength = 1500;
      setReviewState((prevState) => ({
        ...prevState,
        [inputFieldName]: inputValue.slice(0, maxLength),
      }));
    }
  };

  const handleFileChange = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;

    const fileSizeInMB = file.size / (1024 * 1024);

    if (fileSizeInMB >= 5) {
      toastNotifyError('File size should be less than 5MB');
      setIsUplading(false);
      return;
    }

    const isMediaTypeImage = file?.type?.startsWith?.('image/');

    const formData = new FormData();
    formData.append('media_type', 'review_rating');
    formData.append('media', file);
    formData.append('store_id', storeInfo.store_id);

    const mediaUrl = await uploadCustomerMediaToS3({
      payload: formData,
      authToken: getAuthToken(),
      type: !isMediaTypeImage ? 'video' : 'image',
    });

    if (!mediaUrl?.error) {
      setReviewState({
        ...reviewState,
        mediaList: [
          ...(reviewState.mediaList || []),
          {
            media_id: 0,
            media_url: mediaUrl,
            temp_media_url: URL.createObjectURL(file),
            media_type: isMediaTypeImage
              ? REVIEW_MEDIA_TYPE.IMAGE
              : REVIEW_MEDIA_TYPE.VIDEO,
          },
        ],
      });
    } else {
      toastNotifyError('something went wrong!');
    }
    // Reset input so selecting the same file again triggers onChange
    input.value = '';
    setIsUplading(false);
  };

  function addImage() {
    mediaInputRef?.current?.click();
  }

  function removeMediaImage(index) {
    setReviewState({
      ...reviewState,
      mediaList: reviewState?.mediaList?.filter(
        (media, currIndex) => currIndex !== index
      ),
    });
  }

  const handleSubmit = () => {
    if (disableSubmitBtn) {
      toastNotifyError('Pease fill all the mendatory fields !');
      return;
    }
    const payload = {
      store_id: storeInfo.store_id,
      order_id,
      review_rating: [
        {
          status: reviewSettings.auto_publish
            ? REVIEW_RATING_STATUS.PUBLISHED
            : REVIEW_RATING_STATUS.NEW,
          id: loggedInUserReview ? loggedInUserReview?.id : 0,
          item_id: product?.id || product?.item_id,
          full_name: reviewState.full_name,
          review: reviewState.review,
          reply: loggedInUserReview ? loggedInUserReview?.reply : '',
          review_seen: false,
          phone: userData.phone,
          rating: reviewState.rating,
          store_item_review_media: reviewState.mediaList,
        },
      ],
    };
    dispatch(
      publishReview(payload, () => {
        toastNotifySuccess(
          publishReviewToastNotificationText(reviewSettings.auto_publish)
        );
        onSubmit && onSubmit();
      })
    );
    onClose();
  };

  return (
    <BasicModal
      show={open}
      onClose={onClose}
      headingClasses="!tw-p-0 !tw-overflow-visible !tw-border-none"
      customClasses="md:!tw-min-w-[740px] !tw-rounded-[8px_8px_0px_0px] md:!tw-rounded-[8px]  !tw-m-0 !tw-overflow-scroll"
    >
      <input
        ref={mediaInputRef}
        type="file"
        accept="image/*,video/*"
        className="tw-hidden"
        onChange={(e) => {
          if (!e?.target?.files?.length) return;
          setIsUplading(true);
          handleFileChange(e);
        }}
      />

      <div className="tw-relative tw-h-full tw-px-[32px] tw-pb-[142px] tw-pt-[36px]">
        <div className="flex items-center mb28px tw-gap-[20px]">
          <div className="border-grey1 tw-relative tw-flex tw-h-[80px] tw-w-[80px] tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[4px] tw-border-[1px] tw-border-solid tw-bg-contain">
            <img
              src={getImageUrlFromCompressedImages(compressedImages, imageUrl, {
                maxImgSize: imgSizeEnums.LOW,
              })}
              width="64px"
              height="64px"
            />
          </div>
          <div className="flex flex-column tw-gap-[16px]">
            <p className="mv0 tw-line-clamp-1 tw-text-[18px]/[18px] tw-font-medium tw-text-black">
              {product?.name || product?.item_name}
            </p>
            {((loggedInUserReview?.rating && !loggedInUserReview?.review) ||
              !IS_MOBILE) && (
              <div className="flex items-center">
                {!IS_MOBILE && (
                  <ReactStars
                    key={`stars_${reviewState.rating}`}
                    size={32}
                    classNames={styles.gap}
                    onChange={handleRatingChange}
                    value={reviewState.rating}
                    count={5}
                  />
                )}
                {loggedInUserReview?.rating && !loggedInUserReview?.review ? (
                  <p className="ml12px mv0 bg-lime-green-color pv4px ph8px f12px fw5 white-color br2 ">
                    Submitted
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>
        {IS_MOBILE && (
          <div className="tw-mb-[32px] tw-flex tw-flex-col tw-gap-[12px]">
            <p className="mv0 tw-text-[16px]/[18px] tw-font-normal tw-text-black">
              Your Rating
            </p>
            <ReactStars
              key={`stars_${reviewState.rating}`}
              size={32}
              classNames={classNames(styles.gap, 'tw-flex tw-gap-[2px]')}
              onChange={handleRatingChange}
              value={reviewState.rating}
              count={5}
            />
          </div>
        )}
        <div className="tw-flex tw-flex-col tw-gap-[32px]">
          {!!reviewSettings?.media_upload_enabled && (
            <div className="tw-flex">
              {!!reviewState?.mediaList?.length || isUploading ? (
                <div className="tw-flex tw-gap-[16px]">
                  {reviewState?.mediaList?.map?.((media, index) => (
                    <div
                      key={media?.temp_media_url || media?.media_url}
                      className="border-grey1 tw-relative tw-flex tw-h-[72px] tw-w-[72px] tw-items-center tw-justify-center tw-rounded-[4px] tw-object-contain"
                    >
                      <Close
                        fontSize="small"
                        className="border-grey1 tw-absolute tw-right-0 tw-top-0 -tw-translate-y-1/2 tw-translate-x-1/2 tw-cursor-pointer tw-rounded-full tw-bg-white"
                        onClick={() => removeMediaImage(index)}
                      />
                      <MediaWithRetry media={media} />
                    </div>
                  ))}
                  <UploadProgress inProgress={isUploading} />
                  {!mediaLimitReached && !isLastMediaUpoading && (
                    <div
                      className="tw-flex tw-h-[72px] tw-w-[72px] tw-items-center tw-justify-center tw-rounded-[4px] tw-border tw-border-dashed"
                      style={{
                        color: themeColor,
                        borderColor: themeColor,
                        backgroundColor: themeColor + '0D',
                      }}
                      onClick={addImage}
                    >
                      <PlusIcon iconColor={themeColor} />
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className="tw-flex tw-h-[48px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-[12px] tw-rounded-[4px] tw-border tw-border-dashed"
                  style={{
                    color: themeColor,
                    borderColor: themeColor,
                    backgroundColor: themeColor + '0D',
                  }}
                  onClick={addImage}
                >
                  <AddAPhotoOutlinedIcon
                    sx={{ transform: 'scaleX(-1)' }}
                    className="tw-mb-[4px] !tw-h-[20px] !tw-w-[20px]"
                    color="inherit"
                  />
                  Share a video or photo
                </div>
              )}
            </div>
          )}
          <div className="flex flex-column  tw-gap-[12px]">
            <p className="mv0 tw-text-[16px]/[18px] tw-font-normal tw-text-black">
              Full Name
            </p>
            <input
              name="full_name"
              value={reviewState.full_name}
              onChange={handleChange}
              placeholder="Enter your name"
              className="border-grey1 tw-rounded-lg tw-p-[16px] tw-text-sm tw-font-normal placeholder:tw-text-[#707070]"
            />
          </div>
          <div className="flex flex-column tw-gap-[12px]">
            <p className="mv0 tw-text-[16px]/[18px] tw-font-normal tw-text-black">
              Product Review
            </p>
            <textarea
              name="review"
              value={reviewState.review}
              onChange={handleChange}
              placeholder="What should other customers know?"
              className="border-grey1 tw-h-[100px] tw-resize-none tw-rounded-lg tw-p-[16px] tw-text-sm tw-font-normal placeholder:tw-text-[#707070]"
            />
          </div>
        </div>
      </div>
      <div className="tw-fixed tw-bottom-0 tw-mt-[30px] tw-flex tw-w-full tw-justify-end tw-gap-[16px] tw-border tw-border-t tw-border-solid tw-border-gray-200 tw-bg-white tw-px-[32px] tw-py-[36px] md:tw-absolute md:tw-border-none">
        {!IS_MOBILE && (
          <CustomizedButton onClick={onClose} variant="tertiary" color="#000000">
            Cancel
          </CustomizedButton>
        )}
        <CustomizedButton
          variant="primary"
          disabled={disableSubmitBtn}
          className={classNames(
            styles.submitBtn,
            '!tw-h-[46px]',
            IS_MOBILE ? '!tw-w-full' : ''
          )}
          onClick={handleSubmit}
          backgroundColor={disableSubmitBtn ? themeColor + '66' : themeColor}
          color="#FFFFFF"
        >
          {IS_MOBILE ? 'Submit Review' : 'Submit'}
        </CustomizedButton>
      </div>
    </BasicModal>
  );
};

export default CustomerReviewModal;
